import React, { useState, useRef, useCallback, useEffect } from 'react';

const DRAG_SENSITIVITY = 5; // px of drag per frame step
const AUTO_SPEED = 1.0;     // playback rate for auto-rotation

const Viewer360 = ({ frames = [], frameCount = 36 }) => {
  const [currentFrame, setCurrentFrame] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false); // ref mirror for use in event closures
  const startXRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const animFrameRef = useRef(null);
  const totalFrames = frames.length > 0 ? frames.length : frameCount;

  const getX = (e) => (e.touches ? e.touches[0].clientX : e.clientX);

  // Draw loop — only draws if video has data and tab is visible
  const startDrawLoop = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const ctx = canvas.getContext('2d');
    const loop = () => {
      if (!document.hidden && video.readyState >= 2) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      }
      animFrameRef.current = requestAnimationFrame(loop);
    };
    animFrameRef.current = requestAnimationFrame(loop);
  }, []);

  const stopDrawLoop = useCallback(() => {
    cancelAnimationFrame(animFrameRef.current);
  }, []);

  const startAutoRotation = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = AUTO_SPEED;
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // Autoplay blocked (common on mobile before user gesture) — stay paused
      });
    }
    startDrawLoop();
  }, [startDrawLoop]);

  const stopAutoRotation = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    stopDrawLoop();
    if (video.duration) {
      setCurrentFrame(Math.round((video.currentTime / video.duration) * totalFrames) % totalFrames);
    }
  }, [stopDrawLoop, totalFrames]);

  const startDrag = useCallback((e) => {
    isDraggingRef.current = true;
    setIsDragging(true);
    startXRef.current = getX(e);
  }, []);

  const onDrag = useCallback(
    (e) => {
      if (!isDraggingRef.current || startXRef.current === null) return;
      // Prevent page scroll while dragging on touch
      if (e.cancelable) e.preventDefault();
      const deltaX = getX(e) - startXRef.current;
      const steps = Math.floor(Math.abs(deltaX) / DRAG_SENSITIVITY);
      if (steps === 0) return;
      const direction = deltaX > 0 ? -1 : 1;
      setCurrentFrame((prev) => ((prev + direction * steps) % totalFrames + totalFrames) % totalFrames);
      startXRef.current = getX(e);
    },
    [totalFrames]
  );

  const stopDrag = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
    startXRef.current = null;
  }, []);

  // Seek video when manually dragging
  const seekVideo = useCallback((frameIndex) => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      video.currentTime = (frameIndex / totalFrames) * video.duration;
      rafRef.current = null;
    });
  }, [totalFrames]);

  useEffect(() => {
    if (frames.length === 0 && isDragging) seekVideo(currentFrame);
  }, [currentFrame, frames.length, isDragging, seekVideo]);

  // Draw seeked frame to canvas + start auto-rotation once metadata loads
  useEffect(() => {
    if (frames.length > 0) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const ctx = canvas.getContext('2d');

    const drawFrame = () => ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const onLoaded = () => {
      video.currentTime = 0;
      // Small delay ensures currentTime = 0 seek completes before playback
      setTimeout(startAutoRotation, 100);
    };

    video.addEventListener('seeked', drawFrame);
    video.addEventListener('loadedmetadata', onLoaded);
    return () => {
      video.removeEventListener('seeked', drawFrame);
      video.removeEventListener('loadedmetadata', onLoaded);
    };
  }, [frames.length, startAutoRotation]);

  // Pause draw loop when tab is hidden, resume when visible
  useEffect(() => {
    if (frames.length > 0) return;
    const onVisibility = () => {
      if (document.hidden) {
        stopDrawLoop();
        videoRef.current?.pause();
      } else if (!isDraggingRef.current) {
        startAutoRotation();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [frames.length, startAutoRotation, stopDrawLoop]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelAnimationFrame(animFrameRef.current);
      cancelAnimationFrame(rafRef.current);
      videoRef.current?.pause();
    };
  }, []);

  const cursorStyle = isDragging ? 'grabbing' : 'grab';

  return (
    <div
      className="w-full h-full select-none"
      style={{ cursor: cursorStyle }}
      onMouseEnter={stopAutoRotation}
      onMouseLeave={() => {
        if (!isDraggingRef.current) startAutoRotation();
        stopDrag();
        startAutoRotation();
      }}
      onMouseDown={startDrag}
      onMouseMove={onDrag}
      onMouseUp={stopDrag}
      onTouchStart={(e) => { stopAutoRotation(); startDrag(e); }}
      onTouchMove={onDrag}
      onTouchEnd={() => { stopDrag(); startAutoRotation(); }}
    >
      {frames.length > 0 ? (
        <img
          src={frames[currentFrame]}
          alt={`360 view frame ${currentFrame + 1}`}
          className="w-full h-full object-cover pointer-events-none"
          draggable={false}
        />
      ) : (
        <>
          <video
            ref={videoRef}
            src="/360.mp4"
            muted
            playsInline
            loop
            preload="metadata"
            style={{ display: 'none' }}
          />
          <canvas
            ref={canvasRef}
            width={480}
            height={848}
            className="w-full h-full object-cover pointer-events-none"
          />
        </>
      )}
    </div>
  );
};

export default Viewer360;
